import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { CreateWidgetSuccess, DeleteWidget, SetWidgets } from 'app/library/widgets/actions';
import { AddWidgets } from 'app/library/widgets/actions/add-entities.action';
import { MaxBrainWidget } from 'app/library/widgets/models/widget.model';
import { WidgetState } from 'app/library/widgets/models/widget.state';
import { SubentityState } from 'app/projects/subentity/src/lib/models/subentity.state';
import { DeselectSelectedWidgets, SearchWidgets, SelectFilteredWidgets, ToggleSelectedWidget } from '../actions';
import { WidgetsStateModel } from './widgets.state-model';

@State<WidgetsStateModel>({
    name: 'widgets',
    defaults: new WidgetsStateModel(),
})
@Injectable()
export class WidgetsState extends SubentityState<MaxBrainWidget> {
    @Selector([WidgetState.getEntities])
    static getFilteredEntities(state: WidgetsStateModel, widgets: MaxBrainWidget[]): MaxBrainWidget[] {
        if (!widgets) {
            return null;
        }

        return widgets
            .filter((item) => state.filtered.indexOf(item.id) !== -1)
            .map((item) => {
                return [state.list.indexOf(item.id), item];
            })
            .sort((a: [number, MaxBrainWidget], b: [number, MaxBrainWidget]) => {
                return a[0] > b[0] ? 1 : b[0] > a[0] ? -1 : 0;
            })
            .map((x: [number, MaxBrainWidget]) => x[1]);
    }

    @Selector()
    static getSelectedEntityIds(state: WidgetsStateModel): string[] {
        return state.selected;
    }

    @Selector()
    static getSearchTerm(state: WidgetsStateModel): string {
        return state.searchTerm;
    }

    constructor(private store: Store) {
        super(null, 0);

        this._propstoSearch = [...MaxBrainWidget.props_to_search];
    }

    @Action(AddWidgets)
    addEntities(ctx: StateContext<WidgetsStateModel>, action: AddWidgets): void {
        const widgets = this.store.selectSnapshot(WidgetState.getEntities) || [];

        this._patchSubentities(ctx, { payload: { subentityIds: action.payload.map((entity) => entity.id) } }, widgets);
    }

    @Action(CreateWidgetSuccess)
    createEntity(ctx: StateContext<WidgetsStateModel>, action: CreateWidgetSuccess): void {
        const widgets = this.store.selectSnapshot(WidgetState.getEntities) || [];

        this._patchSubentities(ctx, { payload: { subentityIds: [action.payload.id] } }, widgets);
    }

    @Action(DeleteWidget)
    deleteEntity({ getState, patchState }: StateContext<WidgetsStateModel>, { payload }: DeleteWidget): void {
        const state = getState();

        patchState({
            list: [...state.list.filter((subentityId) => subentityId !== payload)],
            selected: [...state.selected.filter((subentityId) => subentityId !== payload)],
            filtered: [...state.filtered.filter((subentityId) => subentityId !== payload)],
        });
    }

    @Action(SetWidgets)
    setEntities(ctx: StateContext<WidgetsStateModel>, action: SetWidgets): void {
        this._setSubentities(ctx, action, null);
    }

    @Action(ToggleSelectedWidget)
    toggleSelect(ctx: StateContext<WidgetsStateModel>, action: ToggleSelectedWidget): void {
        this._toggleSelectedSubentity(ctx, action);
    }

    @Action(SelectFilteredWidgets)
    selectAll(ctx: StateContext<WidgetsStateModel>): void {
        this._selectFilteredSubentities(ctx);
    }

    @Action(DeselectSelectedWidgets)
    deselectAll(ctx: StateContext<WidgetsStateModel>): void {
        this._deselectSelectedSubentities(ctx);
    }

    @Action(SearchWidgets)
    search(ctx: StateContext<WidgetsStateModel>, action: SearchWidgets): void {
        const widgets = this.store.selectSnapshot(WidgetState.getEntities);

        this._searchSubentities(ctx, action, widgets);
    }
}
